// src/components/races/detail/raceDetailTabs.tsx
import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { type RaceWithParticipants } from "@/backend/domain/race/getRace";
import { TabGroup, type Tab } from "@/components/shared/other/tabGroup";
import { ParticipantTable } from './participants/participantTable';
import { ResultsView } from './results/resultsView';
import { StartListView } from './startlist/startListView';

type MainTabType = 'registrations' | 'startlist' | 'results';
type ResultsTabType = 'class' | 'gender' | 'team';
type StartListTabType = 'number' | 'class';

interface RaceDetailTabsProps {
    race: RaceWithParticipants;
    searchTerm: string;
    onSearch: (term: string) => void;
}

export function RaceDetailTabs({ race, searchTerm }: RaceDetailTabsProps) {
    const hasResults = race.status === 'COMPLETED';

    const [mainTab, setMainTab] = useState<MainTabType>(
        hasResults ? 'results' :
            race.participants.some(p => p.startNumber) ? 'startlist' :
                'registrations'
    );

    const [resultsTab, setResultsTab] = useState<ResultsTabType>('class');
    const [startListTab, setStartListTab] = useState<StartListTabType>('number');
    const tabsRef = useRef<HTMLDivElement>(null);

    // Function to ensure tabs are visible
    const ensureTabsVisible = () => {
        if (tabsRef.current) {
            const tabsRect = tabsRef.current.getBoundingClientRect();
            if (tabsRect.top < 40) {
                tabsRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
            }
        }
    };

    // Handle tab changes
    const handleTabChange = (id: string) => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
        setMainTab(id as MainTabType);
    };

    const handleResultsTabChange = (id: string) => {
        ensureTabsVisible();
        setResultsTab(id as ResultsTabType);
    };

    const handleStartListTabChange = (id: string) => {
        ensureTabsVisible();
        setStartListTab(id as StartListTabType);
    };

    // Main tabs that are always shown
    const mainTabs: Tab[] = [
        { id: 'registrations', label: 'Nennungen' }
    ];

    // Add start list if we have start numbers
    if (race.participants.some(p => p.startNumber)) {
        mainTabs.push({ id: 'startlist', label: 'Startliste' });
    }

    // Add results if we have them
    if (hasResults) {
        mainTabs.push({ id: 'results', label: 'Ergebnisse' });
    }

    // Results sub-tabs
    const resultsTabs: Tab[] = [
        { id: 'class', label: 'Nach Klasse' },
        { id: 'gender', label: 'Nach Geschlecht' }
    ];

    if (race.modes.includes('TEAM')) {
        resultsTabs.push({ id: 'team', label: 'Nach Team' });
    }

    const startListTabs: Tab[] = [
        { id: 'number', label: 'Nach Startnr.' },
        { id: 'class', label: 'Nach Klasse' }
    ];

    return (
        <div className="space-y-8">
            {/* Fixed position tabs for better mobile experience */}
            <div
                ref={tabsRef}
                className="sticky top-10 md:top-20 bg-white/80 backdrop-blur-sm pt-4 pb-4 -mt-4 z-10 space-y-4"
            >
                <TabGroup
                    tabs={mainTabs}
                    activeTab={mainTab}
                    onTabChange={handleTabChange}
                />

                {mainTab === 'results' && (
                    <TabGroup
                        tabs={resultsTabs}
                        activeTab={resultsTab}
                        onTabChange={handleResultsTabChange}
                        variant="secondary"
                    />
                )}

                {mainTab === 'startlist' && (
                    <TabGroup
                        tabs={startListTabs}
                        activeTab={startListTab}
                        onTabChange={handleStartListTabChange}
                        variant="secondary"
                    />
                )}
            </div>

            {/* Content area */}
            <div>
                <AnimatePresence mode="wait">
                    {mainTab === 'registrations' && (
                        <motion.div
                            key="registrations"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.2 }}
                        >
                            <ParticipantTable
                                participants={race.participants.filter(p =>
                                    p.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                    p.lastName.toLowerCase().includes(searchTerm.toLowerCase())
                                )}
                                showPlaceOfLiving={race.ask_for_place_of_living}
                                showDisciplines={race.allowSnowboard}
                                showTeam={race.modes.includes('TEAM')}
                                initialSort="name"
                            />
                        </motion.div>
                    )}

                    {mainTab === 'startlist' && (
                        <motion.div
                            key="startlist"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.2 }}
                        >
                            <StartListView
                                race={race}
                                searchTerm={searchTerm}
                                viewMode={startListTab}
                            />
                        </motion.div>
                    )}

                    {mainTab === 'results' && (
                        <motion.div
                            key="results"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.2 }}
                        >
                            <ResultsView
                                race={race}
                                searchTerm={searchTerm}
                                viewMode={resultsTab}
                            />
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}
